import { useEffect, useState } from "react";
import {
  Box,
  Text,
  Link as ChakraLink,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import maplibregl from "maplibre-gl";
import { captureException } from "@sentry/nextjs";

import useApiEndpoint from "hooks/useApiEndpoint";
import { useDetailState } from "scenes/OfferDetail";
import { IMarginProps } from "src/types/types";
import VillagePartDescriptionApiEndpoint from "utils/geo/api/VillagePartDescription";
import { EOfferStatus } from "utils/offer";
import Button from "components/Chakra/Button";
import HtmlParser from "components/Chakra/HtmlParser";
import { DetailData } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";

const Locality: React.FC<IMarginProps & { mapID?: string }> = ({
  mapID,
  ...margins
}) => {
  const [description, setDescription] = useState("");
  const [isLimitedDescription, setIsLimited] = useState(true);
  const { callEndpoint: villagePartDescriptionApi } = useApiEndpoint(
    VillagePartDescriptionApiEndpoint,
  );

  const { offer, status, isLoaded } = useDetailState();

  useEffect(() => {
    if (offer) {
      const mapInst = new maplibregl.Map({
        container: `offer-mapbox${mapID}`, // container id
        style: "https://tiles.ulovdomov.cz/styles/basic-preview/style.json",
        center: [
          offer?.geoCoordinates?.lng ?? 0,
          offer?.geoCoordinates?.lat ?? 0,
        ], // starting position [lng, lat]
        zoom: 15, // starting zoom,
        boxZoom: true,
        interactive: true,
      });

      mapInst.on("load", () => {
        mapInst.addControl(new maplibregl.NavigationControl({}));
        if (document) {
          const compass = document.getElementsByClassName(
            "maplibregl-ctrl-compass",
          ) as HTMLCollectionOf<HTMLElement>;
          const zoomOut = document.getElementsByClassName(
            "maplibregl-ctrl-zoom-out",
          ) as HTMLCollectionOf<HTMLElement>;
          const zoomIn = document.getElementsByClassName(
            "maplibregl-ctrl-zoom-in",
          ) as HTMLCollectionOf<HTMLElement>;
          [...compass, ...zoomOut, ...zoomIn].forEach((element) => {
            element.style.width = "48px";
            element.style.height = "48px";
          });
        }

        const marker = new maplibregl.Marker({ color: "#E94932" });

        marker.getElement().setAttribute("role", "figure");

        marker.setLngLat([offer.geoCoordinates.lng, offer.geoCoordinates.lat]);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        marker.addTo(mapInst);
      });

      const callVillagePartDescriptionApi = async (passOffer: DetailData) => {
        if (!passOffer.villagePart) {
          return;
        }
        try {
          const { data } = await villagePartDescriptionApi({
            villagePartId: passOffer.villagePart?.id,
          });
          if (!data) {
            return;
          }
          setDescription(data.description);
        } catch (err) {
          captureException(err);
        }
      };

      callVillagePartDescriptionApi(offer);
    }
  }, [offer, isLoaded]);

  return (
    <Box w="100%" {...margins}>
      <Skeleton isLoaded={isLoaded}>
        <Box id={`offer-mapbox${mapID}`} height="290px" mb="20px" />
      </Skeleton>

      <SkeletonText isLoaded={isLoaded}>
        {description && (
          <Box>
            <Text
              pb="10px"
              lineHeight="20px"
              data-test="offerDetail.locality.description"
            >
              <HtmlParser
                text={
                  isLimitedDescription
                    ? `${description.slice(0, 200)}...</p>`
                    : description
                }
                values={(html) => (
                  <ChakraLink
                    textColor={
                      status === EOfferStatus.ACTIVE
                        ? "ud-primary"
                        : "ud-grey-600"
                    }
                    fontWeight="bold"
                    href={html.parsedHtml.href}
                    target="_blank"
                    data-test="offerDetail.locality.description.link"
                  >
                    {html.value}
                  </ChakraLink>
                )}
              />
            </Text>
            {isLimitedDescription && (
              <Button
                variant="link"
                onClick={() => setIsLimited(false)}
                data-test="offerDetail.locality.description.more"
              >
                Více o lokalitě
              </Button>
            )}
          </Box>
        )}
      </SkeletonText>
    </Box>
  );
};

export default Locality;
